const stooqMap = {
    AMD: "amd.us",
    NVDA: "nvda.us",
    TSLA: "tsla.us",
    AAPL: "aapl.us",
    MSFT: "msft.us",
    SPY: "spy.us",
    QQQ: "qqq.us",
    "EUR/USD": "eurusd",
    "GBP/USD": "gbpusd",
    "USD/JPY": "usdjpy",
    "XAU/USD": "xauusd"
};
const twelveDataAssetTypes = ["stock", "ETF"];
export class TwelveDataProvider {
    apiKey;
    baseUrl;
    id = "twelve-data";
    name = "Twelve Data";
    constructor(apiKey, baseUrl = "https://api.twelvedata.com") {
        this.apiKey = apiKey;
        this.baseUrl = baseUrl;
    }
    supports(assetType, interval) {
        return twelveDataAssetTypes.includes(assetType) && ["1day", "15min", "1h"].includes(interval);
    }
    async getCandles(symbol, interval, range = {}) {
        const url = new URL(`${this.baseUrl}/time_series`);
        url.searchParams.set("symbol", symbol);
        url.searchParams.set("interval", interval);
        url.searchParams.set("apikey", this.apiKey);
        url.searchParams.set("format", "JSON");
        url.searchParams.set("outputsize", String(range.outputSize ?? 260));
        if (range.startDate)
            url.searchParams.set("start_date", range.startDate);
        if (range.endDate)
            url.searchParams.set("end_date", range.endDate);
        const response = await fetch(url);
        if (!response.ok)
            throw new Error(`Twelve Data candle request failed: ${response.status}`);
        const body = (await response.json());
        if (!body.values?.length)
            throw new Error(body.message ?? `No Twelve Data candles returned for ${symbol}`);
        const candles = body.values.map(normalizeTwelveDataCandle).reverse();
        return {
            symbol,
            provider: this.name,
            meta: {
                source: this.name,
                asOf: candles.at(-1).t,
                isDelayed: true,
                isComplete: interval === "1day",
                interval
            },
            candles,
            warning: "Twelve Data free Basic can be delayed. Daily signals use closed bars; intraday alerts are informational only."
        };
    }
    async getLatestPrice(symbol) {
        const url = new URL(`${this.baseUrl}/quote`);
        url.searchParams.set("symbol", symbol);
        url.searchParams.set("apikey", this.apiKey);
        url.searchParams.set("format", "JSON");
        const response = await fetch(url);
        if (!response.ok)
            throw new Error(`Twelve Data quote request failed: ${response.status}`);
        const body = (await response.json());
        const price = Number(body.close ?? body.price);
        if (!Number.isFinite(price))
            throw new Error(body.message ?? `No Twelve Data latest price returned for ${symbol}`);
        const asOf = body.datetime ? new Date(body.datetime).toISOString() : body.timestamp ? new Date(body.timestamp * 1000).toISOString() : new Date().toISOString();
        return {
            symbol,
            price,
            meta: {
                source: this.name,
                asOf,
                isDelayed: true,
                isComplete: false,
                interval: "15min"
            }
        };
    }
}
export class StooqDailyProvider {
    id = "stooq-daily";
    name = "Stooq Daily CSV";
    supports(assetType, interval) {
        return interval === "1day" && ["stock", "ETF", "forex", "metal"].includes(assetType);
    }
    async getCandles(symbol, assetOrInterval = "1day", range = {}) {
        const interval = isInterval(assetOrInterval) ? assetOrInterval : "1day";
        const stooqSymbol = stooqMap[symbol.toUpperCase()] ?? symbol.toLowerCase().replace("/", "");
        const url = `https://stooq.com/q/d/l/?s=${encodeURIComponent(stooqSymbol)}&i=d`;
        const response = await fetch(url);
        if (!response.ok)
            throw new Error(`Stooq request failed: ${response.status}`);
        const text = await response.text();
        const candles = parseCsvCandles(text).slice(-(range.outputSize ?? 700));
        if (!candles.length)
            throw new Error(`No candles returned for ${symbol}`);
        return {
            symbol,
            provider: this.name,
            meta: {
                source: this.name,
                asOf: candles.at(-1).t,
                isDelayed: true,
                isComplete: true,
                interval
            },
            candles,
            warning: "Stooq daily data is delayed/historical and is a fallback provider."
        };
    }
    async getLatestPrice(symbol) {
        const result = await this.getCandles(symbol, "1day", { outputSize: 1 });
        return {
            symbol,
            price: result.candles.at(-1).c,
            meta: { ...result.meta, isComplete: false }
        };
    }
    async fetchDaily(symbol, assetType) {
        if (!this.supports(assetType, "1day"))
            throw new Error(`Unsupported asset type: ${assetType}`);
        return this.getCandles(symbol, "1day");
    }
}
export class ManualCsvProvider {
    records;
    id = "manual-csv";
    name = "Manual CSV";
    constructor(records) {
        this.records = records;
    }
    supports(_assetType, interval) {
        return interval === "1day";
    }
    async getCandles(symbol, interval, range = {}) {
        const record = [...this.records]
            .reverse()
            .find((item) => item.symbol.toUpperCase() === symbol.toUpperCase() && item.interval === interval);
        if (!record)
            throw new Error(`No manual CSV candles available for ${symbol}`);
        const candles = parseCsvCandles(record.csv).slice(-(range.outputSize ?? 700));
        if (!candles.length)
            throw new Error(`Manual CSV import for ${symbol} did not contain usable candles`);
        return {
            symbol,
            provider: this.name,
            meta: {
                source: this.name,
                asOf: candles.at(-1).t,
                isDelayed: true,
                isComplete: true,
                interval
            },
            candles,
            warning: "Manual CSV data is user-imported fallback data. Verify the file source and timestamp before acting."
        };
    }
    async getLatestPrice(symbol) {
        const result = await this.getCandles(symbol, "1day", { outputSize: 1 });
        return {
            symbol,
            price: result.candles.at(-1).c,
            meta: { ...result.meta, isComplete: false }
        };
    }
}
export class MarketDataRouter {
    providers;
    constructor(providers) {
        this.providers = providers;
    }
    async getCandles(symbol, assetType, interval, range) {
        const errors = [];
        for (const provider of this.providers) {
            if (!provider.supports(assetType, interval))
                continue;
            try {
                return await provider.getCandles(symbol, interval, range);
            }
            catch (error) {
                errors.push(`${provider.name}: ${error instanceof Error ? error.message : "unknown error"}`);
            }
        }
        throw new Error(`No market data provider returned candles for ${symbol}. ${errors.join(" | ")}`);
    }
    async getLatestPrice(symbol, assetType) {
        const errors = [];
        for (const provider of this.providers) {
            if (!provider.supports(assetType, "15min") && !provider.supports(assetType, "1day"))
                continue;
            try {
                return await provider.getLatestPrice(symbol);
            }
            catch (error) {
                errors.push(`${provider.name}: ${error instanceof Error ? error.message : "unknown error"}`);
            }
        }
        throw new Error(`No market data provider returned latest price for ${symbol}. ${errors.join(" | ")}`);
    }
}
export function createDefaultMarketDataRouter(twelveDataApiKey, manualCsvRecords = []) {
    const providers = [];
    if (twelveDataApiKey)
        providers.push(new TwelveDataProvider(twelveDataApiKey));
    providers.push(new StooqDailyProvider());
    if (manualCsvRecords.length)
        providers.push(new ManualCsvProvider(manualCsvRecords));
    return new MarketDataRouter(providers);
}
export function parseCsvCandles(csv) {
    const lines = csv.trim().split(/\r?\n/);
    const header = lines.shift()?.toLowerCase().split(",").map((item) => item.trim()) ?? [];
    const find = (name) => header.findIndex((item) => item === name || item.includes(name));
    const indexes = {
        date: Math.max(find("date"), find("time")),
        open: find("open"),
        high: find("high"),
        low: find("low"),
        close: find("close"),
        volume: find("volume")
    };
    if (indexes.date < 0 || indexes.open < 0 || indexes.high < 0 || indexes.low < 0 || indexes.close < 0) {
        return [];
    }
    return lines
        .map((line) => line.split(","))
        .map((parts) => {
        const timestamp = new Date(parts[indexes.date]);
        return {
            t: Number.isNaN(timestamp.getTime()) ? "" : timestamp.toISOString(),
            o: Number(parts[indexes.open]),
            h: Number(parts[indexes.high]),
            l: Number(parts[indexes.low]),
            c: Number(parts[indexes.close]),
            v: indexes.volume >= 0 ? Number(parts[indexes.volume]) || undefined : undefined
        };
    })
        .filter((candle) => candle.t && Number.isFinite(candle.o) && Number.isFinite(candle.h) && Number.isFinite(candle.l) && Number.isFinite(candle.c));
}
function normalizeTwelveDataCandle(candle) {
    return {
        t: new Date(candle.datetime).toISOString(),
        o: Number(candle.open),
        h: Number(candle.high),
        l: Number(candle.low),
        c: Number(candle.close),
        v: candle.volume ? Number(candle.volume) || undefined : undefined
    };
}
function isInterval(value) {
    return ["1day", "15min", "1h", "4h"].includes(value);
}
